import { useState, memo, useMemo } from 'react';
import { Wheel } from 'react-custom-roulette';
import { REWARD_IDS } from '../../constants';
import '../../css/NewUserSpinEventModal.css';
import useSpinReward from '../../hooks/useSpinReward';

const createWheelItem = (id, imageName, sizeMultiplier = 1.4) => ({
	option: id,
	image: { uri: `/spin-event/${imageName}`, offsetY: 150, sizeMultiplier },
});

const data = [
	createWheelItem(REWARD_IDS.POINTS_20, 'haleon-20-points.png', 0.9),
	createWheelItem(REWARD_IDS.TNG_RM3, 'haleon-tng3.png'),
	createWheelItem(REWARD_IDS.POINTS_120, 'haleon-120-points.png'),
	createWheelItem(REWARD_IDS.TNG_RM10, 'haleon-tng10.png', 0.9),
	createWheelItem(REWARD_IDS.POINTS_70, 'haleon-70-points.png'),
	createWheelItem(REWARD_IDS.TNG_RM5, 'haleon-tng5.png'),
];

const MemoizedWheel = memo(
	({ mustStartSpinning, prizeNumber, onStopSpinning }) => (
		<Wheel
			backgroundColors={['#30e700', 'white']}
			startingOptionIndex={0}
			outerBorderColor="black"
			outerBorderWidth={7.5}
			radiusLineWidth={0}
			pointerProps={{
				src: '/spin-event/wheel-pointer.png',
				style: {
					position: 'absolute',
					left: '50%',
					top: '50%',
					transform: 'translate(-50%, -50%) rotate(45deg)',
					width: '28%',
					zIndex: 10,
				},
			}}
			mustStartSpinning={mustStartSpinning}
			prizeNumber={prizeNumber}
			data={data}
			onStopSpinning={onStopSpinning}
		/>
	),
);

const NewUserSpinEventModal = ({ isOpen, onClose }) => {
	const { spinReward } = useSpinReward();
	const [mustSpin, setMustSpin] = useState(false);
	const [isSpinning, setIsSpinning] = useState(false);
	const [prizeNumber, setPrizeNumber] = useState(0);
	const [selectedRewardId, setSelectedRewardId] = useState();
	const [disableSpinButton, setDisableSpinButton] = useState(false);

	const handleSpinClick = async () => {
		setIsSpinning(true);
		if (mustSpin) return;
		const reward = await spinReward();

		const matchingIndices = data
			.map((item, idx) => (item.option === reward.id ? idx : -1))
			.filter((idx) => idx >= 0);

		if (matchingIndices.length === 0) {
			console.error('Reward id not found in wheel data!');
			return;
		}

		const chosenIndex =
			matchingIndices.length > 1
				? matchingIndices[Math.floor(Math.random() * matchingIndices.length)]
				: matchingIndices[0];

		setPrizeNumber(chosenIndex);
		setSelectedRewardId(data[chosenIndex].option);
		setMustSpin(true);
		setIsSpinning(false);
	};

	const handleStopSpinning = useMemo(
		() => () => {
			onClose(selectedRewardId);
			setMustSpin(false);
		},
		[selectedRewardId],
	);

	if (!isOpen) return null;

	return (
		<div className="spin-modal-overlay">
			<div className="spin-modal-backdrop" />

			<div className="spin-modal-content">
				<div className="spin-wheel-section">
					<svg
						onClick={() => onClose(undefined)}
						style={{
							zIndex: 100,
							cursor: 'pointer',
							position: 'absolute',
							top: '1rem',
							right: '1rem',
						}}
						className="vector"
						width="12"
						height="12"
						viewBox="0 0 12 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M0.923077 12L0 11.0769L5.07692 6L0 0.923077L0.923077 0L6 5.07692L11.0769 0L12 0.923077L6.92308 6L12 11.0769L11.0769 12L6 6.92308L0.923077 12Z"
							fill="black"
						/>
					</svg>
					<img
						src="/spin-event/top-left-balloon.png"
						alt="Balloon decoration"
						className="spin-decoration-tl-balloon"
					/>
					<img
						src="/spin-event/bottom-left-balloon.png"
						alt="Balloon decoration"
						className="spin-decoration-bl-balloon"
					/>
					<img
						src="/spin-event/bottom-right-balloon.png"
						alt="Balloon decoration"
						className="spin-decoration-br-balloon"
					/>
					<img
						src="/spin-event/top-right-balloon.png"
						alt="Balloon decoration"
						className="spin-decoration-rt-balloon"
					/>
					<img
						src="/spin-event/flag-banner-left.png"
						alt="Left banner"
						className="spin-decoration-flag-left"
					/>
					<img
						src="/spin-event/flag-banner-right.png"
						alt="Right banner"
						className="spin-decoration-flag-right"
					/>
					<img
						src="/spin-event/confetti-1.png"
						alt="Confetti"
						className="spin-decoration-confetti"
					/>
					<img
						src="/spin-event/confetti-2.png"
						alt="Confetti"
						className="spin-decoration-confetti"
					/>
					<div className="spin-wheel-container">
						<div className="wheel-wrapper">
							<MemoizedWheel
								mustStartSpinning={mustSpin}
								prizeNumber={prizeNumber}
								onStopSpinning={handleStopSpinning}
							/>
						</div>
					</div>
				</div>

				<div className="spin-text-content">
					<h1 className="spin-title">A Little Extra for You!</h1>
					<p className="spin-description">
						You've unlocked a one-time Welcome Spin — stand a chance to win
						extra points or eWallet rewards!
					</p>

					<button
						disabled={mustSpin || isSpinning}
						onClick={handleSpinClick}
						className="spin-button"
					>
						{mustSpin ? 'Spinning...' : 'Spin Now!'}
					</button>

					<div className="spin-terms-container">
						<p className="spin-terms-text">
							One-time reward for new members only.{' '}
							<span
								onClick={() => window.open('https://www.google.com', '_blank')}
								className="spin-terms-link"
							>
								T&Cs apply
							</span>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default NewUserSpinEventModal;
